import React, { useState } from 'react';
import { formatMoney } from '../../../utils/currency';
import type { CurrencyCode } from '@bbpos/types';
import { Avatar } from '../../BasicComponents/Avatar';
import { Card } from '../../CompositeComponents/Card';
import { Tabs } from '../../Nav_DataComponents/Tabs';
import { RfmTagBadge } from '../RfmTagBadge';
import { IdentityValueScore } from '../IdentityValueScore';
import type { Customer360DashboardProps } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(paise: number, currency: CurrencyCode) {
  return formatMoney(paise, currency);
}

function relativeDate(date: Date): string {
  // No visit yet arrives as the epoch (or an invalid date): show nothing, not "56y ago".
  if (!(date instanceof Date) || !(date.getTime() > 0)) return '—';
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading customer profile">
      <div className="flex items-center gap-3">
        <span className="block h-10 w-10 rounded-full bg-neutral-200 animate-pulse-soft shrink-0" />
        <div className="flex-1 space-y-2">
          <span className="block h-4 w-32 rounded bg-neutral-200 animate-pulse-soft" />
          <span className="block h-3 w-24 rounded bg-neutral-200 animate-pulse-soft" />
        </div>
      </div>
      {[1, 2, 3].map((i) => (
        <span key={i} className="block h-4 rounded bg-neutral-200 animate-pulse-soft" />
      ))}
    </div>
  );
}

// ─── Compact variant ──────────────────────────────────────────────────────────

function CompactView({ props }: { props: Customer360DashboardProps }) {
  const currency = props.currency ?? 'INR';
  const { customer, stats, loyaltyBalance = 0, storeCredit, rfmTag, identityScore, onViewHistory } = props;

  return (
    <div className="space-y-3">
      {/* Visit duration alert */}
      {stats.visitDuration !== undefined && stats.visitDuration > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-info-700 bg-info-50 border border-info-200 rounded-md px-3 py-1.5">
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 5v3.5L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          Customer here for {stats.visitDuration} min{stats.visitDuration > 1 ? 's' : ''}
        </div>
      )}

      {/* Profile row */}
      <div className="flex items-center gap-3">
        <Avatar name={customer.name} src={customer.avatar} size="md" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-neutral-900 text-sm truncate">{customer.name}</p>
          <p className="text-xs text-neutral-500">{customer.phone}</p>
        </div>
        {rfmTag && <RfmTagBadge tag={rfmTag} size="sm" />}
      </div>

      {/* Overdue credit warning */}
      {storeCredit && storeCredit.overdue && (
        <div role="alert" className="flex items-center gap-1.5 text-xs text-danger-700 bg-danger-50 border border-danger-300 rounded-md px-3 py-1.5">
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 2L14 13H2L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M8 7v2M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          Overdue credit: {fmt(storeCredit.outstanding, currency)}
        </div>
      )}

      {/* Quick stats */}
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md bg-neutral-50 px-2.5 py-2">
          <dt className="text-neutral-500">Last visit</dt>
          <dd className="font-semibold text-neutral-800">{relativeDate(stats.lastVisit)}</dd>
        </div>
        <div className="rounded-md bg-neutral-50 px-2.5 py-2">
          <dt className="text-neutral-500">Visits</dt>
          <dd className="font-semibold text-neutral-800">{stats.visitCount}</dd>
        </div>
        <div className="rounded-md bg-neutral-50 px-2.5 py-2">
          <dt className="text-neutral-500">Loyalty pts</dt>
          <dd className="font-semibold text-brand-700">{loyaltyBalance.toLocaleString('en-IN')}</dd>
        </div>
        <div className="rounded-md bg-neutral-50 px-2.5 py-2">
          <dt className="text-neutral-500">Lifetime</dt>
          <dd className="font-semibold text-neutral-800">{fmt(stats.lifetimeSpend, currency)}</dd>
        </div>
        <div className="rounded-md bg-neutral-50 px-2.5 py-2">
          <dt className="text-neutral-500">Avg bill</dt>
          <dd className="font-semibold text-neutral-800">{fmt(stats.avgBillValue ?? 0, currency)}</dd>
        </div>
        {storeCredit && (
          <div className="rounded-md bg-neutral-50 px-2.5 py-2">
            <dt className="text-neutral-500">Store credit</dt>
            <dd className="font-semibold text-success-700">{fmt(storeCredit.outstanding, currency)}</dd>
          </div>
        )}
      </dl>

      {identityScore !== undefined && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-neutral-500">Identity score</span>
          <IdentityValueScore score={identityScore} variant="compact" />
        </div>
      )}

      {onViewHistory && (
        <button
          type="button"
          onClick={onViewHistory}
          className="w-full text-xs text-brand-600 font-medium py-1.5 rounded-md border border-brand-200 hover:bg-brand-50 transition-colors"
        >
          View purchase history
        </button>
      )}
    </div>
  );
}

// ─── Full variant ─────────────────────────────────────────────────────────────

const FULL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'loyalty', label: 'Loyalty' },
  { id: 'credit', label: 'Credit' },
  { id: 'products', label: 'Top Products' },
];

function FullView({ props }: { props: Customer360DashboardProps }) {
  const currency = props.currency ?? 'INR';
  const {
    customer,
    stats,
    topProducts = [],
    preferredCategories = [],
    loyaltyBalance = 0,
    storeCredit,
    rfmTag,
    identityScore,
    onViewHistory,
  } = props;

  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="space-y-4">
      {/* Profile header */}
      <Card variant="outlined" padding="md">
        <div className="flex items-start gap-4">
          <Avatar name={customer.name} src={customer.avatar} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-bold text-neutral-900 text-base">{customer.name}</h2>
              {rfmTag && <RfmTagBadge tag={rfmTag} size="sm" />}
            </div>
            <p className="text-sm text-neutral-500">{customer.phone}</p>
            {customer.email && <p className="text-sm text-neutral-500">{customer.email}</p>}
          </div>
          {identityScore !== undefined && (
            <IdentityValueScore score={identityScore} variant="gauge" />
          )}
        </div>
      </Card>

      {/* Overdue credit warning */}
      {storeCredit?.overdue && (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm text-danger-700 bg-danger-50 border border-danger-300 rounded-lg px-4 py-2.5"
        >
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 2L14 13H2L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M8 7v2M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          Overdue credit balance: <strong>{fmt(storeCredit.outstanding, currency)}</strong>
        </div>
      )}

      {/* Tabs */}
      <Tabs items={FULL_TABS} activeId={activeTab} onChange={setActiveTab} variant="default" />

      {/* Tab panels */}
      {activeTab === 'overview' && (
        <section aria-labelledby="tab-overview">
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Lifetime Spend', value: fmt(stats.lifetimeSpend, currency) },
              { label: 'Total Visits', value: stats.visitCount.toString() },
              { label: 'Avg Bill', value: fmt(stats.avgBillValue, currency) },
              { label: 'Last Visit', value: relativeDate(stats.lastVisit) },
            ].map(({ label, value }) => (
              <Card key={label} variant="outlined" padding="sm">
                <dt className="text-xs text-neutral-500 mb-0.5">{label}</dt>
                <dd className="font-bold text-neutral-900">{value}</dd>
              </Card>
            ))}
          </dl>

          {preferredCategories.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-2">Preferred Categories</h3>
              <div className="flex flex-wrap gap-1.5">
                {preferredCategories.map((cat) => (
                  <span
                    key={cat}
                    className="inline-block text-xs bg-brand-50 text-brand-700 border border-brand-200 rounded-full px-2.5 py-0.5"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}

          {onViewHistory && (
            <button
              type="button"
              onClick={onViewHistory}
              className="mt-4 text-sm text-brand-600 font-medium hover:underline"
            >
              View full purchase history →
            </button>
          )}
        </section>
      )}

      {activeTab === 'loyalty' && (
        <section aria-labelledby="tab-loyalty">
          <Card variant="outlined" padding="md">
            <p className="text-sm text-neutral-500 mb-1">Points Balance</p>
            <p className="text-3xl font-bold text-brand-700 tabular-nums">
              {loyaltyBalance.toLocaleString('en-IN')}
              <span className="text-base font-normal text-neutral-500 ml-1">pts</span>
            </p>
          </Card>
        </section>
      )}

      {activeTab === 'credit' && (
        <section aria-labelledby="tab-credit">
          {storeCredit ? (
            <Card
              variant="status"
              statusColor={storeCredit.overdue ? 'danger-500' : 'success-500'}
              padding="md"
            >
              <p className="text-sm text-neutral-500 mb-1">Outstanding Balance</p>
              <p
                className={[
                  'text-2xl font-bold tabular-nums',
                  storeCredit.overdue ? 'text-danger-700' : 'text-neutral-900',
                ].join(' ')}
              >
                {fmt(storeCredit.outstanding, currency)}
              </p>
              {storeCredit.overdue && (
                <p className="text-xs text-danger-600 mt-1 font-medium">Payment overdue</p>
              )}
            </Card>
          ) : (
            <p className="text-sm text-neutral-500">No credit account</p>
          )}
        </section>
      )}

      {activeTab === 'products' && (
        <section aria-labelledby="tab-products">
          {topProducts.length === 0 ? (
            <p className="text-sm text-neutral-500">No purchase data yet</p>
          ) : (
            <ul className="space-y-2">
              {topProducts.slice(0, 5).map((product, i) => {
                const maxCount = topProducts[0]?.purchaseCount ?? 1;
                const pct = Math.round((product.purchaseCount / maxCount) * 100);
                return (
                  <li key={product.name} className="flex items-center gap-3">
                    <span className="text-xs text-neutral-400 w-4 tabular-nums">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-sm mb-0.5">
                        <span className="font-medium text-neutral-800 truncate">{product.name}</span>
                        <span className="text-neutral-500 tabular-nums ml-2 shrink-0">
                          ×{product.purchaseCount}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-brand-400 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

// ─── Customer360Dashboard ─────────────────────────────────────────────────────

export function Customer360Dashboard({
  loading = false,
  variant = 'compact',
  className,
  ...rest
}: Customer360DashboardProps) {
  if (loading) {
    return (
      <div className={className}>
        <LoadingSkeleton />
      </div>
    );
  }

  const borderClass =
    rest.storeCredit?.overdue ? 'border-2 border-danger-400 rounded-lg p-3' : '';

  return (
    <div className={[borderClass, className ?? ''].filter(Boolean).join(' ')}>
      {variant === 'compact' ? (
        <CompactView props={{ ...rest, variant, loading }} />
      ) : (
        <FullView props={{ ...rest, variant, loading }} />
      )}
    </div>
  );
}
